import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useMemo} from 'react';
import {ContextAction} from 'webapp-lib/Functions';

import {Folder, FolderData} from './Folder';
import {SubFolderTree} from './SubFolderTree';

export type FolderTreeProps = {
  className?: string;
  selectedFolderId?: string;
  /**
   * Whether to fallback to the root folder as selected item.
   * If value is set to false and no selectedFolderId is passed then there will be no active element.
   * Default is true
   */
  fallbackSelectedFolderIdToRoot?: boolean;
  rootFolder: Folder;
  isDraggable?: boolean;
  /**
   * An optional list of data transfer type keys which the dragged item must
   * contain so that it can be dropped (e.g. 'text/lexian-source-folder-id')
   */
  allowedDataTransferKeys?: string[];
  customToolTipText?: string;
  showIconOnly?: boolean;
  /**
   * An optional function that returns they key-value pairs that should be set
   * on the dragged item. These values can then be used to identify the item
   * and where it can be dropped (see allowedDataTransferKeys)
   */
  setDataOnDragStart?: (folder: Folder) => {[key: string]: string};
  onElementDropped?: (
    parameters: Record<string, string>,
    folder: Folder
  ) => void;
  onSelectedFolderChanged?: (folder: Folder) => void;
  onFolderDeleted?: (folder: Folder) => void;
  extraActionsGenerator?: (folder: Folder) => ContextAction<Folder>[];
  loadSubFolders?: (folderId: string) => Promise<FolderData[]>;
  testId?: string;
  /**
   * Called when the Enter key is pressed inside the folder tree.
   * Must be a memoized function.
   */
  onEnter?: () => void;
};

const UFolderTree = ({
  className,
  selectedFolderId,
  fallbackSelectedFolderIdToRoot = true,
  rootFolder,
  isDraggable,
  allowedDataTransferKeys = [],
  customToolTipText,
  showIconOnly = false,
  setDataOnDragStart,
  onElementDropped,
  onSelectedFolderChanged,
  extraActionsGenerator,
  onFolderDeleted,
  onEnter,
  loadSubFolders,
  testId,
}: FolderTreeProps): React.ReactElement => {
  const activeItem = useMemo(() => {
    if (selectedFolderId) {
      return rootFolder.find(selectedFolderId);
    } else {
      return fallbackSelectedFolderIdToRoot ? rootFolder : undefined;
    }
  }, [selectedFolderId, rootFolder, fallbackSelectedFolderIdToRoot]);

  const handleSelect = useCallback(
    (folder: Folder) => {
      if (folder.isDisabled) {
        return;
      }
      if (onSelectedFolderChanged) {
        onSelectedFolderChanged(folder);
      }
    },
    [onSelectedFolderChanged]
  );

  const handleExpand = useCallback(
    async (folder: Folder) => {
      if (
        !folder.isExpandable ||
        folder.isExpanded ||
        folder.isExpansionChevronDisabled
      ) {
        return;
      }
      const subFolderData = loadSubFolders
        ? await loadSubFolders(folder.id)
        : [];
      folder.setSubFolders(subFolderData);
      folder.isExpanded = true;
    },
    [loadSubFolders]
  );

  const handleCollapse = useCallback((folder: Folder) => {
    if (
      !folder.isExpandable ||
      !folder.isExpanded ||
      folder.isExpansionChevronDisabled
    ) {
      return;
    }
    folder.setSubFolders([]);
    folder.isExpanded = false;
  }, []);

  const handleDelete = useCallback(
    (folder: Folder) => {
      onFolderDeleted?.(folder);
    },
    [onFolderDeleted]
  );

  const keyDownHandler = useCallback(
    (event: React.KeyboardEvent) => {
      if (!activeItem) {
        return;
      }
      const {key} = event;
      if (key === 'ArrowDown') {
        const nextItem = activeItem.getNextInTree();
        if (nextItem) {
          handleSelect(nextItem);
        }
        event.preventDefault();
        event.stopPropagation();
      } else if (key === 'ArrowUp') {
        const previousItem = activeItem.getPreviousInTree();
        if (previousItem) {
          handleSelect(previousItem);
        }
        event.preventDefault();
        event.stopPropagation();
      } else if (key === 'ArrowRight') {
        handleExpand(activeItem);
        event.preventDefault();
        event.stopPropagation();
      } else if (key === 'ArrowLeft') {
        handleCollapse(activeItem);
        event.preventDefault();
        event.stopPropagation();
      } else if (
        (key === 'Delete' || key === 'Backspace') &&
        activeItem.parent !== undefined
      ) {
        handleDelete(activeItem);
        event.preventDefault();
        event.stopPropagation();
      } else if (key === 'Enter') {
        onEnter?.();
      }
    },
    [
      activeItem,
      handleExpand,
      handleSelect,
      handleDelete,
      handleCollapse,
      onEnter,
    ]
  );

  const handleDragStart = useCallback(
    (event: React.DragEvent, folder: Folder) => {
      if (setDataOnDragStart) {
        const data = setDataOnDragStart(folder);
        Object.keys(data).forEach((k) => {
          event.dataTransfer.setData(k, data[k]);
        });
      }
      event.dataTransfer.effectAllowed = 'move';
    },
    [setDataOnDragStart]
  );

  return (
    <FolderTreeWrapper
      className={`FolderTreeWrapper ${className}`}
      data-testid={testId ? `FolderTree-${testId}` : undefined}
      // make the table accessible to keyboard navigation
      role='listbox'
      tabIndex={0}
      onKeyDown={keyDownHandler}
    >
      <ul>
        <SubFolderTree
          allowedDataTransferKeys={allowedDataTransferKeys}
          customToolTipText={customToolTipText}
          extraActionsGenerator={extraActionsGenerator}
          folder={rootFolder}
          level={0}
          selectedFolderId={activeItem?.id}
          showIconOnly={showIconOnly}
          onCollapse={handleCollapse}
          onElementDropped={onElementDropped}
          onExpand={handleExpand}
          onSelected={handleSelect}
          {...(isDraggable
            ? {
                isDraggable,
                onDragStart: handleDragStart,
              }
            : {})}
        />
      </ul>
    </FolderTreeWrapper>
  );
};
export const FolderTree = observer(UFolderTree);

const FolderTreeWrapper = styled.div`
  ul {
    margin: 0;
    padding: 0;

    li {
      list-style-type: none;
    }
  }
`;
